import UsersService from 'sharebase/services/usersService';
import { 
	PhoenixUser, 	
	PhoenixUserGroup,
	isUserGroup 
} from 'phoenix/domain';
import { IUserGroupsService } from 'sharebase/services/userGroupsService';
import { IUserHistoryDataStore } from 'sharebase/dataStores/userHistoryDataStore';
import { UserSelectListDialog } from 'sharebase/components/userListSelectDialog/userListSelectDialog';
import { DeploymentNameService } from 'sharebase/services';
import { 
	AuthService, 
	ProgressService
} from 'phoenix/services';

type Selectable = InternalSelectable | ExternalSelectable;
type InternalSelectable = PhoenixUserGroup | PhoenixUser;

export interface ExternalSelectable {
	emailAddress:string;
};

const template = require('./userSelect.template');

require('./userSelect.styles');

/**
 * @ngdoc directive
 * @name sharebase:sbUserSelect
 * @module sharebase
 * @restrict e
 * @description
 * `sbUserSelect` creates an autocompelte/chips input for selected users and/or groups.
 *
 * @usage
 *
 * <sb-user-select placholder="{{Select a user | translate}}" multiple="true" /> 
 * 
 * @element input
 * @param {string=} placeholder A placeholder prompt while not typing, required for single select.
 * @param {string=} floatLabel A label that floats above the input, only valid in non-multi select.
 * @param {boolean=} excludeCurrentUser If this value and include users is true, the autocomplete will be remove the current user from the list.
 * @param {boolean=} includeUsers If true, includes users for the current deployment in the autocomplete list.
 * @param {boolean=} includeUsergroups If true, includes usergroups for the current deployment in the autocomplete list.
 * @param {boolean=} includeExternalEmails If true, allows external emails not associated with users for the current deployment in the autocomplete list.
 * @param {boolean=} includeListView If true, an optional dialogue shows a list of all available user and usergroup selections.
 * @param {boolean=} readonly If true, prevents selecting users/groups beyond what is provided.
 * @param {boolean=} required If true, a valid user/group is required, only valid in non-multi select.
 * @param {string=} requiredMessage A specific message to show on required error message, only valid in non-multi select.
 * @param {array=} selected Pre-populates the input with a list of pre-selected choices.
 * @param {boolean=} multiple If true, a multi select UI is used return array of selected items.
 * @param {boolean=} autofocus If true, the autocomplete will be focused on when the containing dialog/bottomSheet/sideNav opens.
 */
export class UserSelect implements ng.IComponentController{
	public static $inject = [
		'$q',
		'$state',
		'$timeout',
		'$mdMedia',
		'pxAuthService',
		'pxProgressService',
		'sbUserSelectListDialog',
		'sbUsersService',
		'sbUserGroupsService',
		'sbDeploymentNameService',
		'sbUserHistoryDataStore',
		'gettextCatalog'
	];

	public loading:boolean = false;
	public multiple:boolean;
	public deploymentId:number;
	public excludeCurrentUser:boolean;
	public includeUsers:boolean;
	public includeUsergroups:boolean;
	public includeRecents:boolean;
	public includeListView:boolean;
	public includeExternalEmail:boolean;
	public selectOnMatch: boolean;
	public readonly:boolean;
	public selected:(PhoenixUser|PhoenixUserGroup)[];
	public searchText:string;
	public minLength:number;
	public autofocus:boolean = typeof this.autofocus === 'boolean' ? this.autofocus : false;
	public requiredMessage:string;
	public translatedRequiredMessage:string;
	public externalEmailIndicator = this.gettextCatalog.getString('External Email Address');
	public onSelectionChange: (args:{selected:InternalSelectable})=>void;
	
	constructor(
 		private $q:ng.IQService, 
		private $state, 
		private $timeout:ng.ITimeoutService, 
		private $mdMedia,		
		private pxAuthService:AuthService,
		private progressService:ProgressService,
		private sbUserSelectListDialog:UserSelectListDialog,
		private userService:UsersService, 
		private userGroupsService:IUserGroupsService, 
		private sbDeploymentNameService:DeploymentNameService, 
		private sbUserHistoryDataStore:IUserHistoryDataStore,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
		this.deploymentId = +(this.deploymentId || $state.params.deploymentId || $state.params.deploymentID);
		this.excludeCurrentUser = typeof(this.excludeCurrentUser) === 'boolean' ? this.excludeCurrentUser : false;
		this.includeUsers = typeof(this.includeUsers) === 'boolean' ? this.includeUsers : true;
		this.includeUsergroups = typeof(this.includeUsergroups) === 'boolean' ? this.includeUsergroups : true;
		this.includeRecents = typeof(this.includeRecents) === 'boolean' ? this.includeRecents : true;
		this.includeListView = typeof(this.includeListView) === 'boolean' ? this.includeListView : false;
		this.includeExternalEmail = typeof(this.includeExternalEmail) === 'boolean' && !this.multiple ? this.includeExternalEmail : false;
		this.selectOnMatch = typeof(this.selectOnMatch) === 'boolean' ? this.selectOnMatch : !this.includeExternalEmail;
		this.minLength = this.includeRecents ? 0 : 1;
		this.translatedRequiredMessage = this.requiredMessage || this.gettextCatalog.getString('Please select a valid value from the list.');
		this.setLoading(true);
	}

	public $onInit() {		
		if (!this.deploymentId) {
			throw new Error('sbUserSelect must have a deploymentId');
		}

		let initTasks = [];
		return this.$q
			.all(initTasks)
			.finally(() => this.setLoading(false));
	};

	public filter(phrase:string) {		
		let result:Selectable[]|ng.IPromise<Selectable[]> = [];	
		if (phrase && phrase.length > 2 && !this.readonly) { 
			let promises:ng.IPromise<any>[] = [];
			phrase = phrase.toLowerCase();
			if(this.includeUsers){
				let getUsersPromise = this.sbDeploymentNameService.resolveSystemName(this.deploymentId)
										.then(systemName => this.userService.getUsersTypeAhead(phrase, systemName));
				
				if(this.excludeCurrentUser) {
					getUsersPromise = getUsersPromise.then(users => users.filter(user => user.UserId !== this.pxAuthService.userId));
				}
				
				promises.push(getUsersPromise);
			}

			if(this.includeUsergroups) {
				promises.push(this.sbDeploymentNameService.resolveSystemName(this.deploymentId)
					.then(systemName => this.userGroupsService.getUserGroupsTypeAhead(phrase, systemName)));
			}				

			result = this.$q.all(promises)
				.then((results:InternalSelectable[][]) => {
					return results.reduce((all, resultArray) => all.concat(resultArray), [])
				})
				.then(results => {
					return results.sort((a, b) => {
						let aName = (isUserGroup(a) ? a.UserGroupName : a.UserName).toLowerCase();
						let bName = (isUserGroup(b) ? b.UserGroupName : b.UserName).toLowerCase();

						if(aName === bName) {
							return 0;
						}
						else {
							return aName > bName ? 1 : -1;
						}
					});
				})
				.then(results => {
					// external emails
					if (this.includeExternalEmail && results.length < 1) {
						let externalUser:ExternalSelectable = {emailAddress: phrase};
						return this.isExternalUser(externalUser) ? [externalUser] : results;
					}
					else {
						return results;
					}
				});
		}
		else if(this.includeRecents){
			let recents:InternalSelectable[] = [];
			if(this.includeUsers){
				let usersInHistory = this.sbUserHistoryDataStore.users;

				if(this.excludeCurrentUser) {
					usersInHistory = usersInHistory.filter(user => user.UserId !== this.pxAuthService.userId);
				}

				recents = recents.concat(usersInHistory);
			}

			if(this.includeUsergroups){
				recents = recents.concat(this.sbUserHistoryDataStore.userGroups);
			}	

			result = recents;
		}		

		return result;
	}

	public itemSelected(selected:InternalSelectable){
		// execute callback if provided
		this.onSelectionChange && this.onSelectionChange({selected});
		
		// don't add external emails to recents store
		if(!selected || this.isExternalUser(selected)) { return;}

		this.sbUserHistoryDataStore.addToHistory(selected);

		if(this.multiple) {
			this.$timeout(() => this.searchText = null, 300);
		}
	}

	public checkExisting(chip:InternalSelectable) {
		let chipId = isUserGroup(chip) ? chip.UserGroupId : chip.UserId;
		let targetId:number;
		let selectedItem:InternalSelectable;
		for (let i = 0; i < this.selected.length; i++) {
			selectedItem = this.selected[i];
			targetId = isUserGroup(selectedItem) ? selectedItem.UserGroupId : selectedItem.UserId;
			if (chipId === targetId) {
				return null;
			}
		}
	}

	private setLoading(isLoading:boolean = false) {
		this.loading = this.progressService.visible = isLoading;
	}

	private openListSelection(e){
		this.sbUserSelectListDialog
			.open(e, this.selected, this.includeUsers, this.includeUsergroups)
			.then((selected:InternalSelectable[]) => {
				this.selected = selected;
			})
			// dialog closed
			.catch(angular.noop);
	}

	private isExternalUser(item):item is ExternalSelectable {
		// https://regex101.com/r/1GIoiy/1
		const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return angular.isDefined(item.emailAddress) && emailRegex.test(item.emailAddress);
	}
}

export const UserSelectComponent:ng.IComponentOptions = {
	template,
	controller: UserSelect,
	controllerAs: 'vm',
	bindings: {
		placeholder: '@',
		excludeCurrentUser: '<',
		includeUsers: '<',
		includeRecents: '<',
		includeUsergroups: '<',
		readonly: '<',
		required: '<',
		requiredMessage: '@',
		selected: '=',
		onSelectionChange: '&',
		multiple: '<',
		deploymentId: '<',
		includeListView: '<',
		autofocus: '<',
		includeExternalEmail: '<',
		selectOnMatch: '<?'
	}
};


// WEBPACK FOOTER //
// ./src/sharebase/components/userSelect/userSelect.ts